import { Alert, Stack } from '@mui/material';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { TFunction } from 'i18next';
import React from 'react';
import { FieldValues, SubmitHandler, UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { ObjectSchema } from 'yup';

import { FormProviderRc } from '@/components/form/FormProviderRC.comp.tsx';
import { SelectSearchFormField } from '@/components/form/SelectSearchFormField.comp.tsx';
import { TextFormField } from '@/components/form/TextFormField.comp.tsx';
import { handleTableSubmitStopPropagation } from '@/components/form/util/handleTableSubmitStopPropagation.util.ts';
import { flagOptions } from '@/utils/flagOptions.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { CreateIndividualCustomerFormModel } from '@/views/customers/createIndividual/model/CreateIndividualCustomerForm.model.ts';
import { useGetGusCustomer } from '@/views/customers/createIndividual/util/useGetGusCustomer.util.ts';
import { CustomerStatusFormField } from '@/views/customers/status/CustomerStatusFormField.comp.tsx';
import { CustomerStatusFieldProps } from '@/views/customers/status/useCustomerStatusField.util.ts';

interface Props<T extends FieldValues = CreateIndividualCustomerFormModel> {
  disabled: boolean;
  formMethods: UseFormReturn<T>;
  isEdit: boolean;
  onCancel?: () => void;
  onSubmit: SubmitHandler<T>;
  statusField: CustomerStatusFieldProps;
  t: TFunction;
  validation: ObjectSchema<T>;
}

export const CustomersIndividualCreateEditForm: React.FC<Props> = props => {
  const { disabled, formMethods, isEdit, onCancel, onSubmit, statusField, t, validation } = props;
  const navigate = useNavigate();
  const { getGusCustomer, isGettingGusCustomer } = useGetGusCustomer();
  const { t: dictT } = useTranslationWithPrefix('utils.dictionaries');

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    navigate(-1);
  };

  const nip = formMethods.watch('nip');
  const nipProvided = nip && (nip.length === 10 || nip.length === 13);

  const handleGetGusCustomer = async () => {
    if (!nip || !nipProvided) {
      return;
    }
    const customer = await getGusCustomer(nip);
    const { address, ...rest } = customer;
    const currentValues = formMethods.getValues();
    formMethods.reset({
      ...currentValues,
      ...rest,
      ...address,
    });
  };

  const flagsOptions = flagOptions(dictT);

  const isDisabled = disabled || isGettingGusCustomer;

  return (
    <FormProviderRc {...formMethods} validation={validation}>
      <form onSubmit={handleTableSubmitStopPropagation(formMethods.handleSubmit(onSubmit))}>
        <Stack>
          <Typography>{isEdit ? t('titleEdit') : t('titleAdd')}</Typography>
          <Divider />
          <Grid container spacing={2} sx={{ py: 2 }}>
            <Grid size={12}>
              <Alert severity="info">{t('announcement.distinguishingLabel')}</Alert>
            </Grid>
            <Grid size={4}>
              <TextFormField disabled={isDisabled} label={t('form.name')} name="name" />
            </Grid>
            <Grid size={4}>
              <TextFormField
                disabled={isDisabled}
                label={t('form.distinguishingLabel')}
                name="distinguishingLabel"
              />
            </Grid>
            <Grid size={4}>
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  alignItems: 'flex-start',
                }}
              >
                <TextFormField disabled={isDisabled} label={t('form.nip')} name="nip" />
                <Button
                  disabled={!nipProvided}
                  onClick={handleGetGusCustomer}
                  sx={{ height: '46px', mt: 1, textAlign: 'center' }}
                >
                  {t('form.button.checkNip')}
                </Button>
              </Stack>
            </Grid>
            <Grid size={4}>
              <SelectSearchFormField label={t('form.flag')} name="flag" options={flagsOptions} />
            </Grid>
            <CustomerStatusFormField
              disabled={isDisabled}
              field={statusField}
              label={t('form.status')}
            />
            <Grid size={12}>
              <Divider flexItem />
            </Grid>
            <Grid size={4}>
              <TextFormField disabled={isDisabled} label={t('form.address.street')} name="street" />
            </Grid>
            <Grid size={4}>
              <Stack direction="row" spacing={2}>
                <TextFormField
                  disabled={isDisabled}
                  label={t('form.address.houseNo')}
                  name="houseNo"
                />
                <TextFormField
                  disabled={isDisabled}
                  label={t('form.address.flatNo')}
                  name="flatNo"
                />
              </Stack>
            </Grid>
            <Grid size={4}>
              <Stack direction="row" spacing={2}>
                <TextFormField
                  disabled={isDisabled}
                  label={t('form.address.locality')}
                  name="locality"
                />
                <TextFormField
                  disabled={isDisabled}
                  label={t('form.address.postCode')}
                  name="postCode"
                />
              </Stack>
            </Grid>
            <Grid size={4}>
              <TextFormField
                disabled={isDisabled}
                label={t('form.address.community')}
                name="community"
              />
            </Grid>
            <Grid size={4}>
              <TextFormField disabled={isDisabled} label={t('form.address.county')} name="county" />
            </Grid>
            <Grid size={4}>
              <TextFormField
                disabled={isDisabled}
                label={t('form.address.province')}
                name="province"
              />
            </Grid>
          </Grid>
          <Divider flexItem />
          <Stack
            direction="row"
            spacing={2}
            sx={{
              justifyContent: 'flex-end',
            }}
          >
            <Button onClick={handleCancel} variant="outlined">
              {t('button.cancel')}
            </Button>
            <Button loading={isDisabled} type="submit">
              {t('button.save')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </FormProviderRc>
  );
};
